import { useState, useEffect, useCallback, createContext, useContext, useMemo } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useAuthStore } from '@/lib/api-functions/api-auth';
import {
  fetchObjects,
  getCachedObjects,
  getCachedDocumentItems,
  getCachedLibraryItems,
  clearGlossaryCache,
  objectsStore,
  GlossaryItem
} from "@/lib/stores/objectsStore";

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

/**
 * Configuration options for the useGlossaryData hook
 */
interface UseGlossaryDataOptions {
  /** Game ID to filter items by. If not provided, uses selected game */
  gameId?: string;
  /** Force reload data from API instead of using cache */
  forceReload?: boolean;
  /** Automatically load data when component mounts */
  autoLoad?: boolean;
  /** Type of items to retrieve: 'glossary', 'document', 'library', or 'all' */
  type?: 'glossary' | 'document' | 'all' | 'library';
}

/**
 * Return values from the useGlossaryData hook
 */
interface UseGlossaryDataReturn {
  // Data - generic name that works for both glossary and document items
  items: ObjectItem[];
  /** @deprecated Use 'items' instead for better clarity */
  glossaryItems: ObjectItem[];
  
  // Loading states
  isLoading: boolean;
  
  // Actions
  loadData: (forceReload?: boolean) => Promise<void>;
  clearCache: () => Promise<void>;
  
  // Cached data access
  getCachedData: () => ObjectItem[];
}

// ============================================================================
// GLOBAL LOADING STATE CONTEXT
// ============================================================================

interface GlobalLoadingState {
  loadingKeys: Set<string>;
  setLoading: (key: string, loading: boolean) => void;
  isGlobalLoading: (key: string) => boolean;
}

const GlobalLoadingContext = createContext<GlobalLoadingState | null>(null);

export const GlobalLoadingProvider = ({ children }: { children: React.ReactNode }) => {
  const [loadingKeys, setLoadingKeys] = useState<Set<string>>(new Set());

  const setLoading = useCallback((key: string, loading: boolean) => {
    setLoadingKeys(prev => {
      const newSet = new Set(prev);
      if (loading) {
        newSet.add(key);
      } else {
        newSet.delete(key);
      }
      return newSet;
    });
  }, []);

  const isGlobalLoading = useCallback((key: string) => {
    return loadingKeys.has(key);
  }, [loadingKeys]);

  return (
    <GlobalLoadingContext.Provider value={{ loadingKeys, setLoading, isGlobalLoading }}>
      {children}
    </GlobalLoadingContext.Provider>
  );
};

const useGlobalLoading = () => {
  const context = useContext(GlobalLoadingContext);
  if (!context) {
    // Fallback to module-level state if context is not available
    return {
      setLoading: (key: string, loading: boolean) => {
        if (loading) {
          (window as any).__glossaryLoadingState = (window as any).__glossaryLoadingState || {};
          (window as any).__glossaryLoadingState[key] = true;
        } else {
          if ((window as any).__glossaryLoadingState) {
            delete (window as any).__glossaryLoadingState[key];
          }
        }
      },
      isGlobalLoading: (key: string) => {
        return (window as any).__glossaryLoadingState?.[key] || false;
      }
    };
  }
  return context;
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get filtered items based on type and game ID
 */
function getFilteredItems(
  gameId: string, 
  type: 'glossary' | 'document' | 'all' | 'library'
): ObjectItem[] {
  if (type === 'document') {
    return getCachedDocumentItems(gameId);
  } else if (type === 'glossary') {
    return getCachedObjects(gameId);
  } else if (type === 'library') {
    return getCachedLibraryItems(gameId);
  } else {
    // type === 'all' - get all items for the game
    const allItems = objectsStore.getData();
    return allItems.filter(item => item.game_id === gameId);
  }
}

/**
 * Log filtered items count for debugging
 */
function logFilteredItemsCount(
  type: 'glossary' | 'document' | 'all', 
  count: number, 
  gameId: string
): void {
  const typeLabel = type === 'all' ? 'all' : `${type} items`;
}

// ============================================================================
// MAIN HOOK
// ============================================================================

/**
 * Hook for managing glossary and document items data
 * 
 * This hook provides a unified interface for working with both glossary items
 * and document items, with automatic caching and real-time updates.
 * 
 * @param options - Configuration options for the hook
 * @returns Object containing items data, loading states, and actions
 * 
 * @example
 * ```tsx
 * const { items, isLoading, loadData } = useGlossaryData({
 *   gameId: 'game-123',
 *   type: 'glossary',
 *   autoLoad: true
 * });
 * ```
 */
export function useGlossaryData({
  gameId,
  forceReload = false,
  autoLoad = true,
  type = 'glossary', // Default to glossary for backward compatibility
}: UseGlossaryDataOptions = {}): UseGlossaryDataReturn {
  const { toast } = useToast();
  const { user } = useAuthStore();
  const [glossaryItems, setGlossaryItems] = useState<ObjectItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { setLoading, isGlobalLoading } = useGlobalLoading();

  // Generate a unique ID for this hook instance for debugging
  const hookId = useMemo(() => Math.random().toString(36).substr(2, 9), []);
  

  // ============================================================================
  // DATA LOADING LOGIC
  // ============================================================================

  /**
   * Load glossary/document items data
   * @param forceReloadData - Force reload from API instead of using cache
   */
  const loadData = useCallback(async (forceReloadData = false) => {
    if (!gameId) {
      console.warn("No game ID available for loading glossary items");
      return;
    }

    // Prevent multiple simultaneous API calls (both local and global)
    const globalKey = `${gameId}-${type}`;
    if (isLoading || isGlobalLoading(globalKey)) {
     
      return;
    }

    setLoading(globalKey, true);

    setIsLoading(true);
    
    try {
      // Fetch items from the API
      const fetchedItems = await fetchObjects(gameId, undefined, toast as any );
      
      // Get filtered items based on type
      const filteredItems = getFilteredItems(gameId, type);
      logFilteredItemsCount(type, filteredItems.length, gameId);
      
      setGlossaryItems(filteredItems);
    } catch (error) {
      console.error("Error loading glossary items:", error);
    } finally {
      setIsLoading(false);
      setLoading(globalKey, false);
    }
  }, [gameId, type, toast, isLoading, setLoading, isGlobalLoading]);

  // ============================================================================
  // CACHE MANAGEMENT
  // ============================================================================

  /**
   * Clear the glossary cache and reset local state
   */
  const clearCache = useCallback(async () => {
    try {
      await clearGlossaryCache();
      setGlossaryItems([]);
      toast({
        title: "Cache cleared",
        description: "Glossary cache cleared",
      });
    } catch (error) {
      console.error("Error clearing cache:", error);
    }
  }, [toast]);

  /**
   * Get cached data without triggering a load operation
   * @returns Array of cached items filtered by game ID and type
   */
  const getCachedData = (): ObjectItem[] => {
    if (!gameId) return [];
    return getFilteredItems(gameId, type);
  };

  // ============================================================================
  // EFFECTS & SUBSCRIPTIONS
  // ============================================================================

  // Auto-load data on mount when gameId is available
  useEffect(() => {
    if (autoLoad && gameId && !isLoading) {
      loadData(forceReload);
    }
  }, [gameId, autoLoad, forceReload, type, isLoading, loadData, hookId]);

  // Subscribe to store changes for real-time updates
  useEffect(() => {
    if (!gameId) return;


    const unsubscribe = objectsStore.subscribe(() => {
      const cachedItems = getFilteredItems(gameId, type);
      logFilteredItemsCount(type, cachedItems.length, gameId);
      
      // Only update if the data has actually changed
      setGlossaryItems(prevItems => {
        if (JSON.stringify(prevItems) !== JSON.stringify(cachedItems)) {
          return cachedItems;
        } else {
        
        return prevItems;
      }});
    });

    return () => {
      unsubscribe();
      // Clean up global loading state when component unmounts
      const globalKey = `${gameId}-${type}`;
      if (isGlobalLoading(globalKey)) {
        setLoading(globalKey, false);
      }
    };
  }, [gameId, type, setLoading, isGlobalLoading, hookId]);

  // Clean up global loading state when dependencies change
  useEffect(() => {
    return () => {
      const globalKey = `${gameId}-${type}`;
      if (isGlobalLoading(globalKey)) {
        setLoading(globalKey, false);
      }
    };
  }, [gameId, type, setLoading, isGlobalLoading, hookId]);

  // Log when hook unmounts
  useEffect(() => {
    return () => {
    };
  }, [hookId, gameId, type]);

  // ============================================================================
  // RETURN VALUES
  // ============================================================================

  return {
    // Data - generic name that works for both glossary and document items
    items: glossaryItems,
    glossaryItems, // Keep for backward compatibility
    
    // Loading states
    isLoading,
    
    // Actions
    loadData,
    clearCache,
    
    // Cached data access
    getCachedData,
  };
} 